/**
 * Lightning Fast, standalone, NO-LOGIN express resume flow.
 *   upload resume + JD  ->  pay  ->  AI generates  ->  preview + download
 *
 * Uses a CLEAN axios instance (no global interceptor) so no stale auth token
 * is ever sent, the backend express endpoints are AllowAny and must not 401.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'react-toastify';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Zap, Download, FileText, Loader, ArrowLeft, Home as HomeIcon } from 'react-feather';
import { Navbar } from './Navbar';
import WalletPayButton from './WalletPayButton';
import TemplateSelector from './TemplateSelector';
import RunnerGame from './RunnerGame';
import ClassicTemplate from './templates/ClassicTemplate';
import ModernTemplate from './templates/ModernTemplate';
import MinimalTemplate from './templates/MinimalTemplate';
import ExecutiveTemplate from './templates/ExecutiveTemplate';

const api = process.env.REACT_APP_API_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const MAX_SIZE = 10 * 1024 * 1024;
const EXPRESS_PRICE_FALLBACK = 99; // cents; only used if payment-config/ is unreachable

// Clean axios, bypasses the global auth interceptor; only sends provider choice.
const express = axios.create({ baseURL: api });
express.interceptors.request.use((cfg) => {
  return cfg;
});

const TEMPLATES = { modern: ModernTemplate, classic: ClassicTemplate, minimal: MinimalTemplate, executive: ExecutiveTemplate };

function ExpressInner() {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [step, setStep]           = useState('upload');   // upload | pay | generating | done
  const [resumeFile, setResume]   = useState(null);
  const [jdFile, setJdFile]       = useState(null);
  const [jdText, setJdText]       = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [paidIntentId, setPaidIntentId] = useState(null); // set once payment succeeds, retries skip re-charging
  const [resume, setResume2]      = useState(null);
  const [missing, setMissing]     = useState([]);   // keywords still absent after generation
  const [rebuildState, setRebuildState] = useState('idle');  // idle | pay | working
  const [rebuildPI, setRebuildPI] = useState(null);           // {id, clientSecret} for the $0.50 add-on
  const [rebuildInfo, setRebuildInfo] = useState(null);       // {added, remaining} after a rebuild
  const [dropped, setDropped]     = useState([]);
  const [template, setTemplate]   = useState(localStorage.getItem('resumeTemplate') || 'modern');
  const [busy, setBusy]           = useState(false);
  const [err, setErr]             = useState('');
  const [price, setPrice]         = useState(null); // cents, from the backend
  const [promo, setPromo]         = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [promoLeft, setPromoLeft] = useState(null); // remaining promo runs, once known

  // Express stays no-login, but a signed-in user can cover the run with the
  // promo code — the allowance is per account, shared with the standard flow.
  const loggedIn = !!Cookies.get('access_token');

  // Price from the backend so the wallet sheet can't show a stale amount after an
  // EXPRESS_PAYMENT_PRICE change (which would make the drift guard abort payments).
  useEffect(() => {
    let alive = true;
    express.get('payment-config/')
      .then(({ data }) => { if (alive && data && data.express) setPrice(data.express); })
      .catch(() => { /* fall back to EXPRESS_PRICE_FALLBACK */ });
    return () => { alive = false; };
  }, []);

  // Live promo count so the box can say how many free runs are left.
  useEffect(() => {
    const token = Cookies.get('access_token');
    if (!token) return;
    axios.get(`${api}promo/status/`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setPromoLeft(data.remaining))
      .catch(() => { /* count is a nicety; the redeem call is the authority */ });
  }, []);

  // RECOVERY: express state lives only in React, so a reload after a successful
  // charge would otherwise lose the paid session forever (no login, no way to
  // name it again). Persist {sid, intent} on charge and restore it on mount so
  // the user lands on "Retry Generation" instead of a second payment.
  useEffect(() => {
    try {
      const raw = localStorage.getItem('express_paid');
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && saved.sid && saved.intent) {
          setSessionId(saved.sid);
          setPaidIntentId(saved.intent);
          setStep('pay');
          setErr('You already paid for this resume. Tap "Retry Generation" to finish.');
          return;
        }
      }
      // Unpaid but uploaded: the files already live server-side, so a user
      // who left to sign in (for the promo code) lands back on the pay step
      // instead of re-uploading. Two-hour shelf life keeps stale sessions
      // from hijacking a genuinely fresh visit.
      const pending = JSON.parse(localStorage.getItem('express_pending') || 'null');
      if (pending && pending.sid && Date.now() - (pending.t || 0) < 2 * 60 * 60 * 1000) {
        setSessionId(pending.sid);
        setStep('pay');
      }
    } catch (_) { /* ignore malformed */ }
  }, []);

  const jdValid = !!jdFile || jdText.trim().length >= 100;
  const canContinue = !!resumeFile && jdValid;
  const priceCents = price || EXPRESS_PRICE_FALLBACK;
  const priceLabel = `$${(priceCents / 100).toFixed(2)}`;

  // ── dropzones ──
  const onResume = useCallback((acc, rej) => {
    if (rej.length) return toast.error('Unsupported file or over 10 MB.');
    if (acc[0]) setResume(acc[0]);
  }, []);
  const onJD = useCallback((acc, rej) => {
    if (rej.length) return toast.error('Unsupported file or over 10 MB.');
    if (acc[0]) { setJdFile(acc[0]); setJdText(''); }
  }, []);
  const DZ_OPTS = {
    maxSize: MAX_SIZE, multiple: false,
    accept: { 'application/pdf': ['.pdf'], 'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'] },
  };
  const rDz = useDropzone({ ...DZ_OPTS, onDrop: onResume });
  const jDz = useDropzone({ ...DZ_OPTS, onDrop: onJD });

  // ── step 1 -> upload files, get session ──
  const goToPay = async () => {
    setBusy(true); setErr('');
    const fd = new FormData();
    fd.append('resume_file', resumeFile);
    if (jdFile) fd.append('jd_file', jdFile);
    else fd.append('jd_file', new File([new Blob([jdText], { type: 'text/plain' })], 'jd.txt'));
    try {
      const { data } = await express.post('express/upload/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSessionId(data.session_id);
      setPaidIntentId(null);   // fresh session, the previous session's payment must not carry over
      localStorage.removeItem('express_paid');
      try { localStorage.setItem('express_pending', JSON.stringify({ sid: data.session_id, t: Date.now() })); } catch (_) {}
      setStep('pay');
    } catch (e) {
      const m = e?.response?.data?.error || 'Upload failed. Please try again.';
      setErr(m); toast.error(m);
    } finally { setBusy(false); }
  };

  // Shared by the card form and the Apple/Google Pay button.
  const createExpressIntent = async () => {
    const { data } = await express.post('express/payment-intent/', { session_id: sessionId });
    return data; // { id, clientSecret, amount }
  };

  // Post-payment: confirm status server-side, then generate. Shared by both paths.
  const afterPaid = async (intentId) => {
    // Remember the paid session so a reload/crash before generation can recover
    // instead of charging again.
    try { localStorage.setItem('express_paid', JSON.stringify({ sid: sessionId, intent: intentId })); } catch (_) {}
    setBusy(true); setErr('');
    try {
      // 'promo' sessions were marked paid server-side at redemption; there is
      // no Stripe intent to confirm, so the status check would only error.
      if (intentId !== 'promo') {
        await express.post('express/payment-status/', { session_id: sessionId, intent_id: intentId });
      }
      setStep('generating');
      const { data: gen } = await express.post('express/generate/', { session_id: sessionId });
      setResume2(gen.resume);
      setDropped(Array.isArray(gen.dropped_keywords) ? gen.dropped_keywords : []);
      setMissing(Array.isArray(gen.missing_keywords) ? gen.missing_keywords : []);
      setStep('done');
      localStorage.removeItem('express_paid');   // generation succeeded, nothing left to recover
      localStorage.removeItem('express_pending');
      toast.success('Your resume is ready!');
    } catch (e) {
      const m = e?.response?.data?.error || 'Payment went through, but generating hit a problem. Please retry, you will NOT be charged again for the same session.';
      setErr(m); toast.error(m); setStep('pay');
    } finally { setBusy(false); }
  };

  // Promo path: redeem burns one run from the account's shared allowance and
  // marks this express session paid server-side, then generation runs exactly
  // as it would after a card payment.
  const redeemPromo = async () => {
    if (!promo.trim() || redeeming || busy) return;
    setRedeeming(true); setErr('');
    try {
      const { data: res } = await axios.post(`${api}promo/redeem/`,
        { session_id: sessionId, code: promo.trim(), flow: 'express' },
        { headers: { Authorization: `Bearer ${Cookies.get('access_token')}` } });
      if (typeof res.remaining === 'number') setPromoLeft(res.remaining);
      toast.success(res.remaining === 1
        ? 'Code applied. This resume is free, and you have 1 promo run left.'
        : `Code applied. This resume is free, and you have ${res.remaining} promo runs left.`);
      setPaidIntentId('promo');
      setRedeeming(false);
      await afterPaid('promo');
      return;
    } catch (e) {
      // "Not valid" only when the server actually said so; other failures
      // (expired session, auth hiccup) get their own truthful message.
      toast.error(e?.response?.data?.message || e?.response?.data?.error
        || 'Could not apply the code. Please try again.');
    }
    setRedeeming(false);
  };

  // The honesty split, computed BEFORE any money is asked for: keywords the
  // first generation already declined ("dropped") can never be added, so they
  // are flagged up front and are never part of the paid offer. Only genuinely
  // placeable keywords are sold.
  const droppedLower = dropped.map(d => String(d).toLowerCase());
  const unplaceable = missing.filter(k => droppedLower.includes(String(k).toLowerCase()));
  const placeable   = missing.filter(k => !droppedLower.includes(String(k).toLowerCase()));

  // The keyword rebuild is a $0.50 add-on: pay once for the session, then the
  // resume is rebuilt with a hard mandate to place each PLACEABLE keyword.
  // Guarantee: if nothing gets added, the fee refunds itself automatically.
  const doRebuild = async () => {
    setRebuildState('working');
    const prev = placeable;
    try {
      const { data: gen } = await express.post('express/generate/', {
        session_id: sessionId, must_include: prev,
      });
      const remaining = Array.isArray(gen.missing_keywords) ? gen.missing_keywords : [];
      const added = Array.isArray(gen.rebuild_added) ? gen.rebuild_added : prev.filter(k => !remaining.includes(k));
      setResume2(gen.resume);
      setDropped(Array.isArray(gen.dropped_keywords) ? gen.dropped_keywords : []);
      setMissing(remaining);
      setRebuildInfo({ added, remaining });
      if (gen.rebuild_refunded) {
        toast.success('Nothing could be added truthfully, so your $0.50 was refunded automatically.');
      } else if (added.length && !remaining.length) {
        toast.success(`All ${added.length} keywords worked in.`);
      } else if (added.length) {
        toast.success(`Worked in ${added.length} of ${prev.length}. The rest need real experience behind them.`);
      } else {
        toast.info('None of these can be added truthfully. Your resume does not yet support them.');
      }
    } catch {
      toast.error('Could not rebuild. Please try again. You will not be charged twice.');
    } finally {
      setRebuildState('idle');
    }
  };

  const startRebuild = async () => {
    setRebuildState('working');
    try {
      const { data: pi } = await express.post('express/rebuild-intent/', { session_id: sessionId });
      if (pi.already_paid) { await doRebuild(); return; }
      setRebuildPI(pi);
      setRebuildState('pay');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not start the rebuild. Please try again.');
      setRebuildState('idle');
    }
  };

  const payAndRebuild = async () => {
    if (!stripe || !elements) return;
    const card = elements.getElement(CardElement);
    if (!card) {
      toast.error('The card box is still loading. Give it a second and press again. Nothing was charged.');
      return;
    }
    setRebuildState('working');
    let charged = false;   // the error message must tell the truth about money
    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(rebuildPI.clientSecret, {
        payment_method: { card },
      });
      if (error) { toast.error(error.message); setRebuildState('pay'); return; }
      if (paymentIntent.status !== 'succeeded') { toast.error('Payment did not complete. Nothing was charged.'); setRebuildState('pay'); return; }
      charged = true;
      await express.post('express/rebuild-status/', { session_id: sessionId, intent_id: paymentIntent.id });
      await doRebuild();
    } catch {
      toast.error(charged
        ? 'Payment succeeded but the rebuild hit a problem. Click the button again; your payment is remembered and you will not be charged twice.'
        : 'The payment could not be started, so nothing was charged. Please try again.');
      setRebuildState('idle');
    }
  };

  // ── step 2 -> pay, then generate ──
  const payAndGenerate = async () => {
    // Already paid but generation failed? Retry generation only, never re-charge.
    if (paidIntentId) return afterPaid(paidIntentId);
    if (!stripe || !elements || busy) return;
    setBusy(true); setErr('');
    try {
      // create intent
      const pi = await createExpressIntent();

      // Backend says this session is already paid, unlock, don't charge again.
      if (pi.already_paid) {
        setPaidIntentId(pi.id);
        return afterPaid(pi.id);
      }

      // confirm card
      const { error, paymentIntent } = await stripe.confirmCardPayment(pi.clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (error) { setErr(error.message); toast.error(error.message); setBusy(false); return; }
      if (paymentIntent.status !== 'succeeded') { setErr('Payment not completed.'); setBusy(false); return; }
      setPaidIntentId(pi.id);   // charged, every later failure retries generation, never the charge
      await afterPaid(pi.id);
    } catch (e) {
      const m = e?.response?.data?.error || 'Something went wrong. Please try again.';
      setErr(m); toast.error(m); setStep('pay'); setBusy(false);
    }
  };

  const download = async (fmt) => {
    setBusy(true);
    try {
      const res = await express.get(`express/download/?session_id=${sessionId}&file_format=${fmt}&template=${template}`, { responseType: 'blob' });
      const type = fmt === 'docx' ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' : 'application/pdf';
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement('a'); a.href = url; a.download = `Resume_${template}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error(`Failed to download ${fmt.toUpperCase()}.`); }
    finally { setBusy(false); }
  };

  const chooseTemplate = (id) => { setTemplate(id); localStorage.setItem('resumeTemplate', id); };
  const Tmpl = TEMPLATES[template] || ModernTemplate;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white px-4 py-10">
      <div className="max-w-4xl mx-auto">

        {/* header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            <HomeIcon className="w-4 h-4" /> Home
          </button>
          <div className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-500" />
            <h1 className="text-xl font-bold text-gray-800">Lightning Fast Resume</h1>
          </div>
          {/* Switchable while choosing (upload/pay); locked once the result is generated */}
        </div>

        {/* progress dots */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {['Upload', 'Pay', 'Download'].map((label, i) => {
            const idx = { upload: 0, pay: 1, generating: 2, done: 2 }[step];
            return (
              <React.Fragment key={label}>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${idx === i ? 'bg-amber-500 text-white' : idx > i ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                  {idx > i ? '✓ ' : ''}{label}
                </span>
                {i < 2 && <span className="w-6 h-px bg-gray-300" />}
              </React.Fragment>
            );
          })}
        </div>

        {err && <div className="mb-4 text-center text-red-500 text-sm">{err}</div>}

        {/* ───── STEP: UPLOAD ───── */}
        {step === 'upload' && (
          <div className="space-y-5">
            <p className="text-center text-gray-500 text-sm">No sign-up needed. Upload, pay once, download your optimised resume.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div {...rDz.getRootProps()} className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition ${rDz.isDragActive ? 'border-amber-500 bg-amber-50' : resumeFile ? 'border-green-400 bg-green-50' : 'border-gray-300 bg-white hover:border-amber-400'}`}>
                <input {...rDz.getInputProps()} />
                <div className="text-4xl mb-2">{resumeFile ? '✅' : '📄'}</div>
                <p className="font-semibold text-gray-700 text-sm">{resumeFile ? resumeFile.name : 'Resume'}</p>
                <p className="text-xs text-gray-400 mt-1">PDF / DOCX · max 10 MB</p>
              </div>
              <div {...jDz.getRootProps()} className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition ${jDz.isDragActive ? 'border-amber-500 bg-amber-50' : jdFile ? 'border-green-400 bg-green-50' : 'border-gray-300 bg-white hover:border-amber-400'}`}>
                <input {...jDz.getInputProps()} />
                <div className="text-4xl mb-2">{jdFile ? '✅' : '📋'}</div>
                <p className="font-semibold text-gray-700 text-sm">{jdFile ? jdFile.name : 'Job Description'}</p>
                <p className="text-xs text-gray-400 mt-1">PDF / DOCX · or paste below</p>
              </div>
            </div>
            {!jdFile && (
              <div className="relative">
                <textarea rows={5} value={jdText} onChange={(e) => setJdText(e.target.value)}
                  placeholder="…or paste the job description here (min 100 characters)"
                  className={`w-full rounded-2xl border p-4 text-sm resize-none focus:outline-none focus:ring-2 ${jdText && jdText.trim().length < 100 ? 'border-orange-300 focus:ring-orange-300' : 'border-gray-300 focus:ring-amber-400'}`} />
                <span className={`absolute bottom-3 right-4 text-xs ${jdText.trim().length >= 100 ? 'text-green-600' : 'text-gray-400'}`}>{jdText.trim().length}/100</span>
              </div>
            )}
            <button onClick={goToPay} disabled={!canContinue || busy}
              className="w-full py-3.5 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-400 disabled:opacity-40 transition flex items-center justify-center gap-2">
              {busy ? <Loader className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
              Continue to Payment, {priceLabel}
            </button>
          </div>
        )}

        {/* ───── STEP: PAY ───── */}
        {step === 'pay' && (
          <div className="max-w-md mx-auto space-y-5">
            <div className="bg-white rounded-2xl border border-amber-100 p-5">
              <div className="flex justify-between items-center mb-3">
                <span className="text-gray-600 text-sm">One-time payment</span>
                <span className="text-2xl font-bold text-gray-800">{priceLabel}</span>
              </div>
              <p className="text-xs text-gray-400">Your AI-optimised resume is generated right after payment.</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              {!paidIntentId && (
                <>
                  {/* Apple Pay / Google Pay. Hidden while the card path is charging
                      (busy) or once paid, so both paths can never charge at once.
                      Rendered only once the real backend price is loaded, the
                      wallet sheet total is immutable, so a fallback could mismatch. */}
                  {price != null && (
                    <WalletPayButton
                      amount={price}
                      label="Lightning Fast Resume"
                      createIntent={createExpressIntent}
                      onPaid={(intentId) => { setPaidIntentId(intentId); return afterPaid(intentId); }}
                      disabled={busy || !stripe}
                    />
                  )}
                  <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 mb-4">
                    <CardElement options={{ style: { base: { fontSize: '15px', color: '#2b2a25', fontFamily: 'EB Garamond, Georgia, serif' } } }} />
                  </div>
                </>
              )}
              <button onClick={payAndGenerate} disabled={!stripe || busy}
                className="w-full py-3.5 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-400 disabled:opacity-50 transition flex items-center justify-center gap-2">
                {busy ? <><Loader className="w-5 h-5 animate-spin" /> Processing…</>
                  : paidIntentId ? <><Zap className="w-5 h-5" /> Retry Generation</>
                  : <><Zap className="w-5 h-5" /> Pay & Generate</>}
              </button>
              <p className="text-center text-xs text-gray-400 mt-3">Secure payment via Stripe · No card data stored</p>
            </div>

            {/* Promo code: signed-in accounts can cover this run from their
                shared allowance. Hidden once a card payment already went
                through — there is nothing left for a code to cover. */}
            {!paidIntentId && (
              <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-5">
                <p className="text-sm font-semibold text-gray-700">Have a promo code?</p>
                {loggedIn ? (
                  <>
                    <div className="flex gap-2 mt-2">
                      <input
                        type="text"
                        value={promo}
                        onChange={(e) => setPromo(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') redeemPromo(); }}
                        placeholder="Enter your code"
                        className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      />
                      <button
                        onClick={redeemPromo}
                        disabled={!promo.trim() || redeeming || busy}
                        className="px-4 py-2 bg-gray-800 text-white rounded-xl text-sm font-semibold hover:bg-gray-700 disabled:opacity-50 transition"
                      >
                        {redeeming ? 'Checking…' : 'Apply'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      A valid code makes this resume free. No card needed.
                      {promoLeft !== null && promoLeft > 0 && (
                        <span className="text-gray-500"> Your account has {promoLeft} promo {promoLeft === 1 ? 'run' : 'runs'} left.</span>
                      )}
                      {promoLeft === 0 && (
                        <span className="text-gray-500"> Your account has used all its promo runs.</span>
                      )}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-gray-500 mt-2">
                    Promo runs are counted per account, so you need to be signed in to use one.{' '}
                    <button onClick={() => navigate('/signin')} className="text-amber-600 font-semibold hover:underline">
                      Sign in
                    </button>{' '}
                    and come back — your files stay right here.
                  </p>
                )}
              </div>
            )}

            <button
              onClick={() => { localStorage.removeItem('express_pending'); setStep('upload'); }}
              className="block mx-auto text-sm text-gray-400 hover:text-gray-600">
              <ArrowLeft className="w-4 h-4 inline" /> Back to files
            </button>
          </div>
        )}

        {/* ───── STEP: GENERATING ───── */}
        {step === 'generating' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader className="w-10 h-10 text-amber-500 animate-spin" />
            <p className="text-gray-700 font-medium">Creating your ATS-optimised resume…</p>
            <p className="text-gray-400 text-sm text-center">One focused draft plus an expert refine, usually well under a minute.</p>
            <RunnerGame />
          </div>
        )}

        {/* ───── STEP: DONE ───── */}
        {step === 'done' && resume && (
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <TemplateSelector selected={template} onChange={chooseTemplate} />
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={() => download('pdf')} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-500 disabled:opacity-50 text-sm">
                <Download className="w-4 h-4" /> Download PDF
              </button>
              <button onClick={() => download('docx')} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 bg-blue-700 text-white rounded-xl font-semibold hover:bg-blue-600 disabled:opacity-50 text-sm">
                <FileText className="w-4 h-4" /> Download Word
              </button>
            </div>

            {/* Honesty notice, keywords deliberately left out */}
            {dropped.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-amber-800 mb-1">
                  ✓ Kept honest, {dropped.length} keyword{dropped.length === 1 ? '' : 's'} left out
                </p>
                <p className="text-xs text-gray-600 mb-2">
                  We didn't add these because your resume doesn't yet support them, claiming them could backfire in an interview. If you genuinely have any, add the experience and regenerate.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {dropped.map((k, i) => (
                    <span key={i} className="text-xs bg-white border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full">{k}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Rebuild result: exactly what was added and what could not be */}
            {rebuildInfo && (
              <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 space-y-2">
                {rebuildInfo.added.length > 0 && (
                  <p className="text-sm text-emerald-700">
                    <span className="font-semibold">Added to your resume:</span> {rebuildInfo.added.join(', ')}
                  </p>
                )}
                {rebuildInfo.remaining.length > 0 && (
                  <>
                    <p className="text-sm text-amber-800">
                      <span className="font-semibold">Could not be added truthfully:</span> {rebuildInfo.remaining.join(', ')}
                    </p>
                    <p className="text-xs text-gray-500">
                      Your resume does not yet contain the experience to support these, and claiming them
                      would backfire in an interview. Treat this list as your study plan: gain or surface the
                      real experience, update your resume, and run it again.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* The verdict the user deserves BEFORE any money changes hands:
                keywords the generation already declined are flagged here, free. */}
            {unplaceable.length > 0 && !rebuildInfo && (
              <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
                <p className="text-sm text-amber-800">
                  <span className="font-semibold">Cannot be added truthfully:</span> {unplaceable.join(', ')}
                </p>
                <p className="text-xs text-gray-500 mt-1.5">
                  Your resume does not yet contain the experience to support these, so no rebuild can
                  include them and we will not charge you to try. Treat this list as your study plan:
                  gain or surface the real experience, update your resume, and run it again.
                </p>
              </div>
            )}

            {/* Placeable keywords only: the $0.50 add-on works them in, with a
                money-back guarantee if nothing gets added. */}
            {placeable.length > 0 && !rebuildInfo && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-amber-800">
                  {placeable.length} keyword{placeable.length === 1 ? ' is' : 's are'} missing but can be worked in.
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  {placeable.slice(0, 8).join(', ')}{placeable.length > 8 ? ` and ${placeable.length - 8} more` : ''}
                </p>

                {rebuildState !== 'pay' && (
                  <button
                    onClick={startRebuild}
                    disabled={rebuildState === 'working'}
                    className="mt-3 flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-500 disabled:opacity-50 transition"
                  >
                    {rebuildState === 'working'
                      ? <><Loader className="w-4 h-4 animate-spin" /> Rebuilding…</>
                      : <>Work {placeable.length === 1 ? 'it' : `all ${placeable.length}`} into the resume · $0.50</>}
                  </button>
                )}

                {rebuildState === 'pay' && (
                  <div className="mt-3 bg-white border border-amber-200 rounded-xl p-3">
                    <p className="text-xs font-semibold text-gray-700 mb-2">
                      One-time add-on for this resume: ${((rebuildPI?.amount ?? 50) / 100).toFixed(2)}
                    </p>
                    <div className="border border-gray-200 rounded-lg p-2.5">
                      <CardElement options={{ style: { base: { fontSize: '15px' } } }} />
                    </div>
                    <div className="flex gap-2 mt-2.5">
                      <button
                        onClick={payAndRebuild}
                        className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-500 transition"
                      >
                        Pay & rebuild
                      </button>
                      <button
                        onClick={() => setRebuildState('idle')}
                        className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <p className="text-[11px] text-amber-700 mt-2">
                  Each one is placed only where your real experience supports it, and if the rebuild cannot
                  add a single one, your $0.50 is refunded automatically.
                </p>
              </div>
            )}

            <div className="bg-white shadow-lg rounded-2xl overflow-auto border border-gray-100">
              <Tmpl resume={resume} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LightningFast() {
  return (
    <>
      <Navbar />
      <Elements stripe={stripePromise}>
        <ExpressInner />
      </Elements>
    </>
  );
}
